import {
  FBChatbotMessage,
  renderTemplate,
  renderTemplatesWithAssets,
  SB,
} from '@play-co/replicant';
import { replicantAssets } from '../creatives/assets';
import { getPetAssetKey, speciesChatbot } from './flex/species';
import { chatbotStaticAssets } from './chatbotStaticAssets';

export const chatbotAssets = {
  ...replicantAssets,
  ...chatbotStaticAssets,
};

export type ChatbotAssetKey = keyof typeof chatbotAssets;

function getImageUrl(imageKeyOrUrl: string, api) {
  if (!imageKeyOrUrl || /^https?:\/\//.test(imageKeyOrUrl)) {
    // Image is already a URL
    return imageKeyOrUrl;
  }

  if (process.env.REPLICANT_ENDPOINT?.includes('localhost')) {
    // Dummy image for localhost testing
    return `https://dummyimage.com/400x400&text=${imageKeyOrUrl}`;
  }

  return api.getAssetPath(imageKeyOrUrl);
}

const chatbotMessageTemplates = renderTemplatesWithAssets(chatbotAssets, {
  comment2play: renderTemplate({
    args: SB.object({
      text: SB.string(),
    }),
    renderers: {
      facebook: ({ api, args }) => ({
        messaging_type: 'UPDATE',
        message: {
          text: args.text,
          quick_replies: [
            {
              content_type: 'text',
              title: 'yes!',
              payload: JSON.stringify({
                messageText: "Uhhh! I'm so excited!",
                buttonText: "Let's Play!",
                payload: { $channel: 'COMMENT' },
              }),
            },
            {
              content_type: 'text',
              title: "No! I don't like you!",
              payload: JSON.stringify({
                messageText: "You're mean! 😭",
                buttonText: 'Humiliate some more',
                payload: { $channel: 'COMMENT' },
              }),
            },
          ],
        },
      }),
      instagram: ({ api, args }) => ({
        messaging_type: 'UPDATE',
        message: {
          text: args.text,
          quick_replies: [
            {
              content_type: 'text',
              title: 'yes!',
              payload: JSON.stringify({
                messageText: "Uhhh! I'm so excited!",
                buttonText: "Let's Play!",
                payload: { $channel: 'COMMENT' },
              }),
            },
            {
              content_type: 'text',
              title: "No! I don't like you!",
              payload: JSON.stringify({
                messageText: "You're mean! 😭",
                buttonText: 'Humiliate some more',
                payload: { $channel: 'COMMENT' },
              }),
            },
          ],
        },
      }),
    },
  }),
  messengerSimple: renderTemplate({
    args: SB.object({
      text: SB.string(),
    }),
    renderers: {
      facebook: ({ api, args }) => ({
        messaging_type: 'UPDATE',
        message: {
          text: args.text,
        },
      }),
      instagram: ({ api, args }) => ({
        messaging_type: 'UPDATE',
        message: {
          text: args.text,
        },
      }),
    },
  }),
  messengerPlayButton: renderTemplate({
    args: SB.object({
      url: SB.string(),
      title: SB.string(),
      subtitle: SB.string().optional(),
      imageUrl: SB.string().optional(),
      cta: SB.string(),
    }),
    renderers: {
      facebook: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                  default_action: {
                    type: 'web_url',
                    url: args.url,
                  },
                  buttons: [
                    {
                      title: args.cta,
                      type: 'web_url',
                      url: args.url,
                    },
                  ],
                },
              ],
            },
          },
        },
      }),
      instagram: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                  default_action: {
                    type: 'web_url',
                    url: args.url,
                  },
                  buttons: [
                    {
                      title: args.cta,
                      type: 'web_url',
                      url: args.url,
                    },
                  ],
                },
              ],
            },
          },
        },
      }),
    },
  }),
  messengerPlainImage: renderTemplate({
    args: SB.object({
      url: SB.string(),
      title: SB.string(),
      subtitle: SB.string().optional(),
      imageUrl: SB.string().optional(),
    }),
    renderers: {
      facebook: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                },
              ],
            },
          },
        },
      }),
      instagram: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                },
              ],
            },
          },
        },
      }),
    },
  }),
  messengerQuickReply: renderTemplate({
    args: SB.object({
      url: SB.string(),
      title: SB.string(),
      subtitle: SB.string().optional(),
      imageUrl: SB.string().optional(),
      cta: SB.string(),
      text: SB.string().optional(),
      quickReplies: SB.array(SB.string()).optional(),
    }),
    renderers: {
      facebook: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          text: args.text || args.title,
          quick_replies: [...args.quickReplies],
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                },
              ],
            },
          },
        },
      }),
      instagram: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          text: args.text || args.title,
          quick_replies: [...args.quickReplies],
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                },
              ],
            },
          },
        },
      }),
    },
  }),
  messengerQuickReplyButtons: renderTemplate({
    args: SB.object({
      url: SB.string(),
      title: SB.string(),
      subtitle: SB.string().optional(),
      imageUrl: SB.string().optional(),
      quickReplyButtons: SB.array(
        SB.object({
          title: SB.string(),
          type: SB.string(),
          url: SB.string().optional(),
        }),
      ),
    }),
    renderers: {
      facebook: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                  default_action: {
                    type: 'web_url',
                    url: args.url,
                  },
                  buttons: [...(args.quickReplyButtons as any)],
                },
              ],
            },
          },
        },
      }),
      instagram: ({ api, payload, args }) => ({
        payload,
        messaging_type: 'UPDATE',
        message: {
          attachment: {
            type: 'template',
            payload: {
              template_type: 'generic',
              image_aspect_ratio: 'square',
              elements: [
                {
                  title: args.title,
                  subtitle: args.subtitle,
                  image_url: getImageUrl(args.imageUrl, api),
                  default_action: {
                    type: 'web_url',
                    url: args.url,
                  },
                  buttons: [...(args.quickReplyButtons as any)],
                },
              ],
            },
          },
        },
      }),
    },
  }),
  petChat: renderTemplate({
    args: SB.object({
      url: SB.string(),
      message: SB.string(),
      species: SB.tuple(speciesChatbot),
      petName: SB.string(),
    }),
    renderers: {
      facebook: ({ api, args }) =>
        facebookGenericTemplate({
          title: args.petName,
          subtitle: args.message,
          image_url: api.getAssetPath(getPetAssetKey(args.species)),
          cta: 'Play Now!',
          url: args.url,
        }),
      instagram: ({ api, args }) =>
        instagramGenericTemplate({
          title: args.petName,
          subtitle: args.message,
          image_url: api.getAssetPath(getPetAssetKey(args.species)),
          cta: 'Play Now!',
          url: args.url,
        }),
    },
  }),

  // TODO: Convert these flex templates into normal templates for Messenger / IG Chatbot
  // Add flex messages templates
  // ...chatbotFlexTemplates,
});

function facebookGenericTemplate(opts: {
  title: string;
  subtitle?: string;
  image_url?: string;
  cta: string;
  url: string;
  image_aspect_ratio?: 'horizontal' | 'square';
}): FBChatbotMessage {
  return {
    messaging_type: 'UPDATE',
    message: {
      attachment: {
        type: 'template',
        payload: {
          template_type: 'generic',
          image_aspect_ratio: opts.image_aspect_ratio,
          elements: [
            {
              title: opts.title,
              subtitle: opts.subtitle,
              default_action: {
                type: 'web_url',
                url: opts.url,
              },
              image_url: opts.image_url,
              buttons: [
                {
                  title: opts.cta,
                  type: 'web_url',
                  url: opts.url,
                },
              ],
            },
          ],
        },
      },
    },
  };
}

function instagramGenericTemplate(opts: {
  title: string;
  subtitle?: string;
  image_url?: string;
  cta: string;
  url: string;
}): any {
  return {
    messaging_type: 'UPDATE',
    message: {
      attachment: {
        type: 'template',
        payload: {
          template_type: 'generic',
          elements: [
            {
              title: opts.title,
              subtitle: opts.subtitle,
              default_action: {
                type: 'web_url',
                url: opts.url,
              },
              image_url: opts.image_url,
              buttons: [
                {
                  title: opts.cta,
                  type: 'web_url',
                  url: opts.url,
                },
              ],
            },
          ],
        },
      },
    },
  };
}

export { chatbotMessageTemplates };
